import { useEffect, useRef, useState, type ReactElement, type ReactNode, type ReactPortal } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-aria-components';
import { Mark } from '../components/Mark';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { IconButton } from '../ui/IconButton';

const HEIGHT_PROPERTY = '--tfw-shell-topbar-height';

/** The white top bar: breadcrumb trail and the search trigger. Publishes its height for sticky page chrome. */
export function TopBar({
  crumbs,
  onSearch,
  onMenu,
  homeHref
}: {
  crumbs: string[];
  onSearch(): void;
  /** Opens the navigation overlay; the menu button shows below md only. */
  onMenu?(): void;
  /** The home link target (the first section); the mark shows below md only. */
  homeHref?: string;
}): ReactElement {
  const bar = useRef<HTMLElement>(null);

  useEffect(() => {
    const node = bar.current;
    const root = document.documentElement;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      root.style.setProperty(HEIGHT_PROPERTY, `${height}px`);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      root.style.removeProperty(HEIGHT_PROPERTY);
    };
  }, []);

  return (
    <header
      ref={bar}
      className="sticky top-0 z-bar flex items-center justify-between gap-4 px-6 py-3 bg-surface border-0 border-b border-solid border-line font-ui"
    >
      <div className="flex items-center gap-2 min-w-0">
        {homeHref ? (
          <Link
            href={homeHref}
            aria-label="ReportPad home"
            className="md:hidden inline-flex items-center justify-center rounded-control no-underline text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            <Mark size={24} />
          </Link>
        ) : null}
        {onMenu ? (
          <div className="flex md:hidden">
            <IconButton label="Open navigation" onPress={onMenu}>
              <Icon name="menu" size={20} />
            </IconButton>
          </div>
        ) : null}
      <nav aria-label="Breadcrumb" className="min-w-0">
        <ol className="m-0 p-0 flex items-center gap-2 list-none text-md text-muted">
          {crumbs.map((crumb, i) => {
            const last = i === crumbs.length - 1;
            // Below md only the current page shows; the literals keep Tailwind emitting both classes.
            return (
              <li key={`${i}-${crumb}`} className={last ? 'm-0 p-0 flex items-center gap-2' : 'm-0 p-0 hidden md:flex items-center gap-2'}>
                {i > 0 ? <span aria-hidden="true" className={last ? 'hidden md:inline' : undefined}>/</span> : null}
                <span aria-current={last ? 'page' : undefined} className={last ? 'text-ink' : 'text-muted'}>
                  {crumb}
                </span>
              </li>
            );
          })}
          {/* min-w-0: a flex item's automatic minimum is its content, and a content-sized title field
              would then widen the page instead of shrinking to the bar (down to its own min-w-fit-field). */}
          <li data-topbar-title-slot="" className="m-0 p-0 flex items-center gap-2 min-w-0 empty:hidden" />
        </ol>
      </nav>
      </div>
      <div className="flex md:hidden shrink-0">
        <IconButton label="Search" onPress={onSearch}>
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="6" />
            <path d="m20 20-4.5-4.5" />
          </svg>
        </IconButton>
      </div>
      {/* A plain wrapper carries the responsive hide: a class on Button would fight its own inline-flex. */}
      <div className="hidden md:flex shrink-0">
        <Button variant="secondary" size="sm" onPress={onSearch}>
          Search or jump to
        </Button>
      </div>
    </header>
  );
}

/** Renders `children` as the top bar's last crumb (after "/"), e.g. the editor's title field. */
export function TopBarTitle({ children }: { children: ReactNode }): ReactPortal | null {
  const [slot, setSlot] = useState<Element | null>(null);
  useEffect(() => {
    setSlot(document.querySelector('li[data-topbar-title-slot]'));
  }, []);
  if (!slot) return null;
  return createPortal(
    <>
      <span aria-hidden="true">/</span>
      {children}
    </>,
    slot
  );
}
